import React, { useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { getBankFormError, isBankFormComplete } from '../helper';
import type { IBankFormValues, TBankFieldError } from '../helper';
import styles from '../styles.module.css';

interface BankFormProps {
  bankRegex?: string;
  ifscRegex?: string;
  isSubmitting?: boolean;
  onSubmit: (values: IBankFormValues) => void;
}

const INITIAL_VALUES: IBankFormValues = {
  accountHolderName: '',
  accountNumber: '',
  confirmAccountNumber: '',
  ifscCode: '',
};

/** Error copy per field, matching Android's messages. */
const ERROR_KEYS: Record<Exclude<TBankFieldError, null>, string> = {
  accountHolderName: 'accountHolderNameRequired',
  accountNumber: 'invalidAccountNumber',
  confirmAccountNumber: 'accountNumberMismatch',
  ifscCode: 'invalidIfsc',
};

const BankForm: React.FC<BankFormProps> = ({
  bankRegex,
  ifscRegex,
  isSubmitting,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<IBankFormValues>(INITIAL_VALUES);
  const [errorField, setErrorField] = useState<TBankFieldError>(null);

  const setField = (field: keyof IBankFormValues) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
    if (errorField === field) setErrorField(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    const invalidField = getBankFormError(values, { bankRegex, ifscRegex });
    if (invalidField) {
      setErrorField(invalidField);
      return;
    }
    setErrorField(null);
    onSubmit(values);
  };

  const renderField = (
    field: keyof IBankFormValues,
    labelKey: string,
    extra?: React.InputHTMLAttributes<HTMLInputElement>,
  ) => {
    const hasError = errorField === field;
    return (
      <div className={styles.field}>
        <label className="fontSizeSm12 fontColor666" htmlFor={`bank-${field}`}>
          {t(labelKey)}
        </label>
        <input
          id={`bank-${field}`}
          className={clsx(styles.input, hasError && styles.inputError)}
          type="text"
          autoComplete="off"
          value={values[field]}
          onChange={setField(field)}
          aria-invalid={hasError}
          aria-describedby={hasError ? `bank-${field}-error` : undefined}
          {...extra}
        />
        {hasError && (
          <p
            id={`bank-${field}-error`}
            className={clsx(styles.fieldError, 'fontSizeSm12')}
            role="alert"
          >
            {t(ERROR_KEYS[field])}
          </p>
        )}
      </div>
    );
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {renderField('accountHolderName', 'accountHolderNameLabel')}
      {renderField('accountNumber', 'accountNumberLabel', {
        inputMode: 'numeric',
      })}
      {renderField('confirmAccountNumber', 'confirmAccountNumberLabel', {
        inputMode: 'numeric',
        // Pasting into the confirm field defeats the point of the check.
        onPaste: (e) => e.preventDefault(),
      })}
      {renderField('ifscCode', 'ifscCodeLabel', {
        autoCapitalize: 'characters',
        spellCheck: false,
      })}

      <button
        type="submit"
        className="btn primaryBtn"
        disabled={!isBankFormComplete(values) || isSubmitting}
      >
        {isSubmitting ? t('pleaseWait') : t('continue')}
      </button>
    </form>
  );
};

export default BankForm;
